import { useCallback, useEffect, useState } from 'preact/hooks';
import type { Notebook } from '../model/types';
import { useApp } from './app-context';
import { BackupPanel } from './BackupPanel';

const fmtDate = (t: number) => new Date(t).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function NotebookList() {
  const { repo, device } = useApp();
  const [all, setAll] = useState<Notebook[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const reload = useCallback(async () => setAll(await repo.listNotebooks(true)), [repo]);
  useEffect(() => void reload(), [reload]);

  const live = all?.filter((n) => n.deletedAt === undefined) ?? [];
  const deleted = all?.filter((n) => n.deletedAt !== undefined) ?? [];

  const create = async (title: string) => {
    const { notebook } = await repo.createNotebook(title, device);
    setCreating(false);
    location.hash = `#/nb/${notebook.id}`;
  };

  return (
    <div class="screen">
      <header class="topbar">
        <h1>Notebooks</h1>
        <div class="spacer" />
        <button class="btn primary" onClick={() => setCreating(true)} disabled={creating}>New notebook</button>
        <a class="btn" href="#/settings">Settings</a>
      </header>
      <main class="content">
        {creating && (
          <TitleForm
            initial="" submitLabel="Create" placeholder="Notebook title"
            onSubmit={create} onCancel={() => setCreating(false)}
          />
        )}
        {all === null ? (
          <p class="hint">Loading…</p>
        ) : live.length === 0 && !creating ? (
          <p class="empty">No notebooks yet. Tap “New notebook” to start.</p>
        ) : (
          <ul class="nb-list">
            {live.map((nb) => <NotebookRow key={nb.id} nb={nb} onChange={reload} />)}
          </ul>
        )}

        {deleted.length > 0 && (
          <section class="deleted">
            <button class="link" onClick={() => setShowDeleted(!showDeleted)}>
              {showDeleted ? 'Hide' : 'Show'} recently deleted ({deleted.length})
            </button>
            {showDeleted && (
              <ul class="nb-list">
                {deleted.map((nb) => (
                  <li key={nb.id} class="nb-row muted">
                    <div class="nb-main">
                      <span class="nb-title">{nb.title}</span>
                      <span class="nb-meta">Deleted {fmtDate(nb.deletedAt!)}</span>
                    </div>
                    <button class="btn" onClick={async () => { await repo.restoreNotebook(nb.id); await reload(); }}>Restore</button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section class="card">
          <h2>Backup</h2>
          <BackupPanel onRestored={reload} />
        </section>
      </main>
    </div>
  );
}

function NotebookRow({ nb, onChange }: { nb: Notebook; onChange: () => void }) {
  const { repo } = useApp();
  const [mode, setMode] = useState<'view' | 'rename' | 'confirm-delete'>('view');

  if (mode === 'rename') {
    return (
      <li class="nb-row">
        <TitleForm
          initial={nb.title} submitLabel="Save" placeholder="Notebook title"
          onSubmit={async (t) => { await repo.renameNotebook(nb.id, t); setMode('view'); onChange(); }}
          onCancel={() => setMode('view')}
        />
      </li>
    );
  }
  if (mode === 'confirm-delete') {
    return (
      <li class="nb-row confirm" role="alertdialog" aria-label={`Delete ${nb.title}?`}>
        <div class="nb-main">
          <span class="nb-title">Delete “{nb.title}”?</span>
          <span class="nb-meta">It moves to “recently deleted” and can be restored. Ink data is never purged.</span>
        </div>
        <button class="btn danger" onClick={async () => { await repo.deleteNotebook(nb.id); onChange(); }}>Delete</button>
        <button class="btn" onClick={() => setMode('view')}>Cancel</button>
      </li>
    );
  }
  return (
    <li class="nb-row">
      <a class="nb-main" href={`#/nb/${nb.id}`}>
        <span class="nb-title">{nb.title}</span>
        <span class="nb-meta">Edited {fmtDate(nb.updatedAt)}</span>
      </a>
      <button class="btn" onClick={() => setMode('rename')}>Rename</button>
      <button class="btn" onClick={() => setMode('confirm-delete')}>Delete</button>
    </li>
  );
}

function TitleForm(props: {
  initial: string; submitLabel: string; placeholder: string;
  onSubmit: (title: string) => void | Promise<void>; onCancel: () => void;
}) {
  const [value, setValue] = useState(props.initial);
  const title = value.trim();
  return (
    <form
      class="title-form"
      onSubmit={(e) => { e.preventDefault(); if (title) void props.onSubmit(title); }}
      onKeyDown={(e) => { if (e.key === 'Escape') props.onCancel(); }}
    >
      <input
        autoFocus value={value} placeholder={props.placeholder} maxLength={200}
        onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)}
      />
      <button class="btn primary" type="submit" disabled={!title}>{props.submitLabel}</button>
      <button class="btn" type="button" onClick={props.onCancel}>Cancel</button>
    </form>
  );
}
